// Copyright (C) Lembani Sakala - All rights reserved.
// Lembani Sakala.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaCar, FaMapMarkerAlt, FaCalendarAlt, FaUserFriends, FaUser, FaEnvelope, FaPhone, FaSuitcase, FaCreditCard, FaEdit } from 'react-icons/fa';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { loadVehicleTypesAsync, selectVehicleTypeById } from '../../../app/vehicle-type/vehicle-typeSlice';

import logo from '../../../images/logo.png';
import { ClipLoader } from 'react-spinners';

import { formatDateTime } from '../../../utils/datetimeFomatter';
import { createBookingAsync } from '../../../app/booking/bookingSlice';
import { EditBookingModalRental } from './EditBookingModalRental';
import { updateBookingAsync } from '../../../app/booking/bookingSlice';
import { createTokenAsync } from '../../../app/payment/paymentSlice';
import { RootState } from '../../../app/store';

const DPO_URL = process.env.REACT_APP_DPO_URL;

export const QuotePreview = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { quote } = useAppSelector((state: RootState) => state.booking);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [vehTyp, setVehTyp] = useState(0);

  const handleModalOpen = (v: any) => {
    setVehTyp(v);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setVehTyp(0);
  };

  const handleBookingSubmit = (values: any) => {
    setLoading(true);
    dispatch(createBookingAsync(values));
    setTimeout(() => {
      navigate('/quote-preview');
    }, 3000);
    setShowModal(false);
    setVehTyp(0);
  };

  const handleEditBookingSubmit = (values: any) => {
    setLoading(true);
    dispatch(updateBookingAsync(values));
    navigate('/processing');
    setTimeout(() => {
      navigate('/quote-preview');
    }, 3000);
    setShowModal(false);
    setVehTyp(0);
  };

  const { paymentToken } = useAppSelector((state: RootState) => state.payment);

  const handlePay = async (id: number) => {
    try {
      const req = await dispatch(createTokenAsync(id));
      navigate('/processing');
      if (req.meta.requestStatus === 'fulfilled') {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        window.location.href = DPO_URL + req.payload?.transToken;
      }
    } catch (error) {
      console.error('Error while processing payment:', error);
    }
  };

  const { vehicleTypes } = useAppSelector((state: RootState) => state.vehicleType);

  useEffect(() => {
    dispatch(loadVehicleTypesAsync());
    setTimeout(() => {
      setLoading(false);
    }, 3000);
  }, [dispatch]);

  const r = parseInt(quote?.booking?.vehicleType);
  const createdDate = new Date(quote?.booking?.createdAt);
  const updatedDate = new Date(quote?.booking?.updatedAt);
  const { formattedDate, formattedTime } = formatDateTime(createdDate);
  const { formattedDate: formattedUpdatedDate, formattedTime: formattedUpdatedTime } = formatDateTime(updatedDate);

  const startDateTime = new Date(quote?.booking?.startDate);
  const endDateTime = new Date(quote?.booking?.endDate);
  const { formattedDate: startDate, formattedTime: startTime } = formatDateTime(startDateTime);
  const { formattedDate: endDate, formattedTime: endTime } = formatDateTime(endDateTime);

  const vt = useAppSelector((state: RootState) => selectVehicleTypeById(state, r));

  try {
    if (loading) {
      return (
        <div className='col-md-5 text-center mx-auto py-4'>
          <ClipLoader size={55} color='#e81f29' className='mx-auto red-txt' />
          <h3 className='text-muted h3'>Loading Quote...</h3>
        </div>
      );
    }

    if (!quote?.booking) {
      return (
        <div className='col-md-5 text-center mx-auto py-4'>
          <h4 className='text-muted h4'>Sorry! Something went wrong while loading your quote.</h4>
          <p className='text-muted lead'>It's not your fault.</p>
          <div className='text-center mx-auto py-3'>
            <Link to='/car-rental/long-and-short-term' className='btn btn-lg btn-r wiggle-btn'>
              Please try again
            </Link>
          </div>
        </div>
      );
    }

    let currency = '';
    if (quote?.booking?.selectedRate === 'ZMW') {
      currency = 'K';
    } else if (quote?.booking?.selectedRate === 'USD') {
      currency = '$';
    }

    const totalAmount = quote?.booking?.fuelCost !== null
      ? (
          parseFloat(quote?.booking?.bookingRateAmount) * parseFloat(quote?.booking?.duration) +
          parseFloat(quote?.booking?.totalDriverAllowance) +
          parseFloat(quote?.booking?.tollFees) +
          parseFloat(quote?.booking?.busFare) +
          parseFloat(quote?.booking?.driverFood) +
          parseFloat(quote?.booking?.driverLodging) +
          parseFloat(quote?.booking?.accessoriesTotal || 0) -
          parseFloat(quote?.booking?.discount)
        ).toFixed(2)
      : (
          parseFloat(quote?.booking?.bookingRateAmount) * parseFloat(quote?.booking?.duration) +
          parseFloat(quote?.booking?.totalDriverAllowance) +
          parseFloat(quote?.booking?.accessoriesTotal || 0) -
          parseFloat(quote?.booking?.discount)
        ).toFixed(2);

    return (
      <>
        <div className='quote-page-wrapper'>
          <div className='quote-container'>

            {/* Quote Header */}
            <div className='quote-header'>
              <div className='quote-header-left'>
                <img src={logo} className='quote-logo' alt='City Drive' />
                <div className='quote-company-info'>
                  <span>citydrive.reservations@gmail.com</span>
                  <span>+260 976 760 159</span>
                </div>
              </div>
              <div className='quote-header-right'>
                <h2 className='quote-number'>Quotation #{quote?.booking?.bookingNumber}</h2>
                <div className='quote-meta'>
                  <span>Issued: {formattedDate} at {formattedTime}</span>
                  {(formattedDate !== formattedUpdatedDate || formattedTime !== formattedUpdatedTime) && (
                    <span>Updated: {formattedUpdatedDate} at {formattedUpdatedTime}</span>
                  )}
                </div>
                <span className={`quote-status-badge ${quote?.booking?.status?.toLowerCase().replace(/\s/g, '-')}`}>
                  {quote?.booking?.status}
                </span>
              </div>
            </div>

            {/* Client & Booking Info Cards */}
            <div className='quote-info-grid'>
              <div className='quote-card'>
                <h4 className='quote-card-title'><FaUser /> Client Details</h4>
                <div className='quote-detail-list'>
                  <div className='quote-detail-item'>
                    <FaUser className='quote-detail-icon' />
                    <span>{quote?.booking?.firstname} {quote?.booking?.lastname}</span>
                  </div>
                  <div className='quote-detail-item'>
                    <FaEnvelope className='quote-detail-icon' />
                    <span>{quote?.booking?.email}</span>
                  </div>
                  <div className='quote-detail-item'>
                    <FaPhone className='quote-detail-icon' />
                    <span>{quote?.booking?.phone}</span>
                  </div>
                </div>
              </div>

              <div className='quote-card'>
                <h4 className='quote-card-title'><FaCar /> Booking Information</h4>
                <div className='quote-detail-list'>
                  <div className='quote-detail-item'>
                    <strong>Vehicle:</strong>
                    <span>{quote?.booking?.vehicleType.type || vt?.type}</span>
                  </div>
                  <div className='quote-detail-item'>
                    <strong>Route:</strong>
                    <span>
                      {quote?.booking?.pickUpTown === quote?.booking?.drivingToTown ? (
                        <>{quote?.booking?.pickUpTown} (Within town)</>
                      ) : (
                        <>
                          {quote?.booking?.pickUpTown} &rarr; {quote?.booking?.drivingToTown}
                          {quote?.booking?.drivingToCountry !== 'Zambia' && (
                            <small className='ms-1'>({quote?.booking?.drivingToCountry})</small>
                          )}
                        </>
                      )}
                    </span>
                  </div>
                  <div className='quote-detail-item'>
                    <strong>Drop-off:</strong>
                    <span>{quote?.booking?.dropOffTown}</span>
                  </div>
                  <div className='quote-detail-item'>
                    <FaCalendarAlt className='quote-detail-icon' />
                    <span>{startDate} {startTime} &mdash; {endDate} {endTime} ({quote?.booking?.duration} days)</span>
                  </div>
                  <div className='quote-detail-row'>
                    <div className='quote-detail-item'>
                      <strong>Drive Mode:</strong>
                      <span>{quote?.booking?.driveMode}</span>
                    </div>
                    <div className='quote-detail-item'>
                      <FaUserFriends className='quote-detail-icon' />
                      <span>{quote?.booking?.numOfAdults} Adults, {quote?.booking?.numOfChildren} Children</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Table */}
            <div className='quote-card quote-invoice-card'>
              <h4 className='quote-card-title'><FaCreditCard /> Invoice Summary</h4>
              <div className='quote-invoice-table'>
                <div className='quote-invoice-header'>
                  <span className='qi-item'>Service Item</span>
                  <span className='qi-rate'>Rate</span>
                  <span className='qi-duration'>Duration</span>
                  <span className='qi-subtotal'>Sub-total</span>
                </div>

                <div className='quote-invoice-row'>
                  <span className='qi-item'>Car Rental</span>
                  <span className='qi-rate'>{currency} {quote?.booking?.bookingRateAmount}</span>
                  <span className='qi-duration'>{quote?.booking?.duration} days</span>
                  <span className='qi-subtotal'>
                    {currency} {(parseFloat(quote?.booking?.bookingRateAmount) * parseFloat(quote?.booking?.duration)).toFixed(2)}
                  </span>
                </div>

                <div className='quote-invoice-row'>
                  <span className='qi-item'>Driver Allowance</span>
                  <span className='qi-rate'>
                    {quote?.booking?.driveMode === 'Self-drive' ? '—' : `${currency} ${quote?.booking?.driverRateAmount}`}
                  </span>
                  <span className='qi-duration'>
                    {quote?.booking?.driveMode === 'Self-drive' ? '—' : `${quote?.booking?.duration} days`}
                  </span>
                  <span className='qi-subtotal'>
                    {quote?.booking?.driveMode === 'Self-drive'
                      ? `${currency} 0.00`
                      : `${currency} ${quote?.booking?.totalDriverAllowance}`}
                  </span>
                </div>

                {quote?.booking?.fuelCost !== null && quote?.booking?.fuelCost > 0 && (
                  <div className='quote-invoice-row'>
                    <span className='qi-item'>One Way Rental Cost</span>
                    <span className='qi-rate'>
                      {currency}{' '}
                      {(
                        parseFloat(quote?.booking?.fuelCost) +
                        parseFloat(quote?.booking?.tollFees) +
                        parseFloat(quote?.booking?.busFare) +
                        parseFloat(quote?.booking?.driverFood) +
                        parseFloat(quote?.booking?.driverLodging)
                      ).toFixed(2)}
                    </span>
                    <span className='qi-duration'>—</span>
                    <span className='qi-subtotal'>
                      {currency}{' '}
                      {(
                        parseFloat(quote?.booking?.fuelCost) +
                        parseFloat(quote?.booking?.tollFees) +
                        parseFloat(quote?.booking?.busFare) +
                        parseFloat(quote?.booking?.driverFood) +
                        parseFloat(quote?.booking?.driverLodging)
                      ).toFixed(2)}
                    </span>
                  </div>
                )}

                {quote?.booking?.accessoriesTotal > 0 && (
                  <div className='quote-invoice-row'>
                    <span className='qi-item'>
                      <FaSuitcase className='me-1' /> Accessories
                      <small className='quote-accessories-detail'>
                        ({quote?.booking?.accessories?.map((acc: string, idx: number) => (
                          <span key={acc}>
                            {acc} - K
                            {acc === 'Jerry Cans' && 25}
                            {acc === 'Satellite Phone' && 250}
                            {acc === 'GPS' && 150}
                            {acc === 'Cooler Box' && 25}
                            {acc === 'Ground tent / Mattresses / Beddings' && 900}
                            {acc === 'Camping Equipments' && 800}
                            {idx < quote?.booking?.accessories?.length - 1 && ', '}
                          </span>
                        ))})
                      </small>
                    </span>
                    <span className='qi-rate'></span>
                    <span className='qi-duration'></span>
                    <span className='qi-subtotal'>{currency} {quote?.booking?.accessoriesTotal}</span>
                  </div>
                )}

                <div className='quote-invoice-row quote-discount-row'>
                  <span className='qi-item'></span>
                  <span className='qi-rate'></span>
                  <span className='qi-duration'>Discount (7%)</span>
                  <span className='qi-subtotal'>— {currency} {quote?.booking?.discount}</span>
                </div>

                <div className='quote-invoice-total'>
                  <span>Total</span>
                  <span>{currency} {totalAmount}</span>
                </div>
              </div>
            </div>

            {/* Payment Notice */}
            <div className='quote-notice'>
              <sup>*</sup> You are required to pay at least 50% of the total amount as down payment for your booking to be reserved.
            </div>

            {/* Action Buttons */}
            <div className='quote-actions'>
              <div className='quote-actions-left'>
                <Link to='/car-rental/long-and-short-term' className='quote-btn quote-btn-secondary'>
                  Cancel
                </Link>
                <button className='quote-btn quote-btn-secondary' onClick={() => handleModalOpen(vt?.id)}>
                  <FaEdit className='me-1' /> Edit
                </button>
              </div>
              <button className='quote-btn quote-btn-primary' onClick={() => handlePay(quote?.booking?.id)}>
                <FaCreditCard className='me-1' /> Proceed to Pay
              </button>
            </div>

            <div className='quote-footer'>
              Thank you for considering <span className='red-txt'>City Drive</span>!
            </div>
          </div>
        </div>
        <EditBookingModalRental
          vt={vehTyp}
          show={showModal}
          onHide={handleModalClose}
          onSubmit={handleEditBookingSubmit}
        />
      </>
    );
  } catch (error) {
    return (
      <div className='col-md-5 text-center mx-auto py-4'>
        <h4 className='text-muted h4'>Sorry! Something went wrong while generating your quote.</h4>
        <p className='text-muted lead'>It's not your fault.</p>
        <div className='text-center mx-auto py-3'>
          <Link to='/car-rental/long-and-short-term' className='btn btn-lg btn-r wiggle-btn'>
            Please try again
          </Link>
        </div>
      </div>
    );
  }
};
